﻿import {$, $$, get, getData, getDataJson, hide, highlight, Key, postData, show} from "@/Utils"
import {
    CategoryTreeIdToUrl,
    SearchNodeResults,
    SearchNodeSuggestions,
    Breadcrumbs,
    SearchNodeDoc,
    SearchNodeGroup
} from "@/interfaces";

let categoryTreeIdLookup: CategoryTreeIdToUrl = null;
const maxTotalResults = 8;
const maxProductResults = 7;
const maxCategoryResults = 15;
const productEntryHeight = 81;
const spareAreaHeightWithoutProducts = 350;

function suggestionsCallback(results: SearchNodeResults) {
    const allResults = get("search-display-all-results") as HTMLAnchorElement;
    const productResultsTitle = get("products-title");
    const productResultsContainer = get("products-pane");
    const documentResultsContainer = get("suggestions-documents");
    const documentResultsTitle = get("suggestions-documents-title");
    const categoryBreadcrumbsTitle = get("suggestions-group-title");
    const categoryBreadcrumbsContainer = get("suggestions-group");

    const isNosto = window.config.search.nostoSearchUrl.length
    if (!results) {
        hide(productResultsTitle);
        productResultsContainer.innerHTML = "";
        documentResultsContainer.innerHTML = "";
        hide(documentResultsTitle)
        categoryBreadcrumbsContainer.innerHTML = "";
        hide(categoryBreadcrumbsTitle)
        return;
    }

    const suggestion = results.search_query;
    const suggestionLo = suggestion.toLowerCase();

    const productTemplate = get("template-product") as HTMLTemplateElement;
    hide(productResultsTitle);
    productResultsContainer.innerHTML = "";

    const searchValue = getSearchString(false);
    const productResults = results.groups.type
        ? results.groups.type.groups.find(x => x.value == "product")
        : {docs: results.docs.map(x => ({
                id: parseInt(x.id),
                code: x.code,
                name: x.name,
                price: parseFloat(x.price),
                normprice: parseFloat(x.normprice),
                url: x.url,
                img: x.img,
                type: x.type
            }) as SearchNodeDoc), doc_count: results.doc_count, value: "product"} as SearchNodeGroup;
    
    if (suggestion === searchValue && !results.doc_count) {
        hide(allResults)
    }
    else {
        show(productResultsTitle);
        const calcProducts = (window.innerHeight - spareAreaHeightWithoutProducts) / productEntryHeight
        const shownProducts = Math.min(maxProductResults, Math.max(1, calcProducts))
        productResults?.docs.slice(0, shownProducts).forEach((x: SearchNodeDoc) => {
            const searchResult = productTemplate.content.cloneNode(true) as HTMLElement;
            searchResult.querySelector("[data-search-item-name]").textContent = x.name;
            
            let normPrice = undefined;
            let discountPrice = undefined;
            if (!x.normprice || x.normprice == x.price) {
                normPrice = x.price;
            } else {
                normPrice = x.normprice;
                discountPrice = x.price;
            }
            
            const priceSection = searchResult.querySelector("[data-search-item-price-section]");
            const discountPriceSection = priceSection.querySelector("[data-search-item-discount-price]");
            priceSection.querySelector("[data-search-item-price]").textContent = `${normPrice} ${window.config.currencySign}`;            
            
            if(discountPrice) {
                discountPriceSection.querySelector("[discount-price]").textContent = `${discountPrice} ${window.config.currencySign}`;
                show(discountPriceSection as HTMLElement);
            }
            else {
                hide(discountPriceSection as HTMLElement);
            }                
            
            const searchElement = searchResult.firstElementChild as HTMLAnchorElement;
            const img = searchResult.querySelector("img");
            
            if (isNosto) {
                searchElement.href = x.url;
                img.src = x.img
                searchElement.setAttribute('code', x.code);                
            } else {
                searchElement.href = window.config.nativeHomeUrl + x.url;
                img.src += x.img.replace(".png", "-mediumsmall.png");
            }           
            
            productResultsContainer.appendChild(searchResult);
        });

        // nosto recordSearchClick
        productResultsContainer.querySelectorAll("[code]").forEach((x) => {
            x.addEventListener("click", (e) => {                
                const productCode = (e.currentTarget as HTMLElement).getAttribute('code');
                const url = (e.currentTarget as HTMLElement).getAttribute('href');
                const script = registerRecordSearchClick(productCode, url);                
                productResultsContainer.appendChild(script);
            });
        })

        // nosto recordSearch
        if(productResults?.docs.length > 0) {
            const script = registerRecordSearch(results);
            productResultsContainer.appendChild(script);
        }
    }

    const contentTemplate = get("template-content") as HTMLTemplateElement;
    const documentTemplate = get("template-document") as HTMLTemplateElement;

    if (productResults?.docs) {
        show(allResults);
        allResults.href = window.config.search.rootSearchUrl + suggestionLo;
        get("search-results-count").textContent = (productResults?.doc_count || 0).toString();
        get("search-results-count-text").textContent = (productResults?.doc_count || 0).toString();
    }

    documentResultsContainer.innerHTML = "";
    const page = "page";
    const promotion = "promotion";
    const controller = "store"; // Naming things
    const pageResults = isNosto ? null : results.groups.type.groups.find(x => x.value === page);
    const pageDocs = pageResults?.docs.slice(0, maxTotalResults) || [];
    pageDocs.forEach(x => x.type = page);
    const promotionResults =  isNosto ? null : results.groups.type.groups.find(x => x.value === promotion);
    const promotionDocs = promotionResults?.docs.slice(0, maxTotalResults) || [];
    promotionDocs.forEach(x => x.type = promotion);
    const controllerResults = isNosto ? null : results.groups.type.groups.find(x => x.value === controller);
    const controllerDocs = controllerResults?.docs.slice(0, maxTotalResults) || [];
    controllerDocs.forEach(x => x.type = controller);
    const interleavedResults = new Array(maxTotalResults).fill(0).map((_, i) => [pageDocs[i], promotionDocs[i], controllerDocs[i]]);
    const contentAndNews = interleavedResults.reduce((a, b) => a.concat(b)).filter(x => x).slice(0, maxTotalResults);

    contentAndNews.sort((a, b) => {
        if (a.type < b.type) {
            return -1;
        }
        if (a.type > b.type) {
            return 1;
        }
        return 0;
    });

    if (contentAndNews.length) {
        show(documentResultsTitle);
        contentAndNews.forEach((x: SearchNodeDoc) => {
            const searchResult = (x.type === "promotion" ? contentTemplate : documentTemplate).content.cloneNode(true) as HTMLElement;
            const searchElement = searchResult.firstElementChild as HTMLAnchorElement;
            searchElement.href = window.config.nativeHomeUrl + x.url;
            searchResult.querySelector("[data-search-item-name]").textContent = x.name;
            documentResultsContainer.appendChild(searchResult);
        });
    }
    else {
        hide(documentResultsTitle);
    }


    const categoryBreadcrumbsTemplate = get("template-group-breadcrumbs") as HTMLTemplateElement;
    const suggestionInCategories: Breadcrumbs[][] = [];
    
    if(!isNosto) {
        const groupMap = categoryTreeIdLookup.groupIdToUrl;
        
        Object.keys(groupMap).forEach(x => {
            if (groupMap[x].title.toLowerCase().includes(suggestionLo)) {
                const breadcrumbs: Breadcrumbs[] = [{
                    url: groupMap[x].url,
                    title: highlight(groupMap[x].title, suggestionLo)
                }];
                suggestionInCategories.push(breadcrumbs);
            }
        });

        const subGroupMap = categoryTreeIdLookup.subGroupIdToUrl;
        Object.keys(subGroupMap).forEach(x => {
            if (suggestionInCategories.length <= maxCategoryResults && subGroupMap[x].title.toLowerCase().includes(suggestionLo)) {
                const breadcrumbs: Breadcrumbs[] = [{
                    url: subGroupMap[x].url,
                    title: highlight(subGroupMap[x].title, suggestionLo)
                }];
                let parentId: string = categoryTreeIdLookup.parentRelations[x];
                while (parentId) {
                    if (categoryTreeIdLookup.parentRelations[parentId]) {
                        const currentUrl = subGroupMap[parentId].url;
                        breadcrumbs.forEach(x => x.url = currentUrl + "/" + x.url);
                        breadcrumbs.push({url: currentUrl, title: subGroupMap[parentId].title});
                    } else {
                        const currentUrl = groupMap[-parentId]?.url;
                        if (currentUrl) {
                            breadcrumbs.forEach(x => x.url = currentUrl + "/" + x.url);
                            breadcrumbs.push({url: currentUrl, title: groupMap[-parentId].title});
                        }
                    }
                    parentId = categoryTreeIdLookup.parentRelations[parentId];
                }
                suggestionInCategories.push(breadcrumbs);
            }
        });
    }
    
    if (contentAndNews.length || suggestionInCategories.length) {
        show(get("suggestions-pane"))
    }

    categoryBreadcrumbsContainer.innerHTML = "";
    if (suggestionInCategories.length) {
        show(categoryBreadcrumbsTitle);
        suggestionInCategories.forEach(y => {
            const breadcrumb = document.createElement("ul");
            breadcrumb.className = "breadcrumbs";
            y.reverse().forEach(z => {
                const breadcrumbPart = categoryBreadcrumbsTemplate.content.cloneNode(true) as HTMLElement;
                const item = breadcrumbPart.querySelector("[data-search-item-name]") as HTMLAnchorElement;
                item.href = window.config.homeUrl + z.url;
                item.innerHTML = z.title;
                breadcrumb.appendChild(breadcrumbPart);
            });
            categoryBreadcrumbsContainer.appendChild(breadcrumb);
        });
    } else {
        hide(categoryBreadcrumbsTitle);
    }
}

function suggestionFacets(suggestion: string) {
    if(window.config.search.nostoSearchUrl.length){
        getDataJson(window.config.search.nostoSearchUrl + "/" +suggestion,
            suggestionsCallback,
            true);
    }
    else {
        getDataJson(window.config.search.searchNodeSearchUrl + "?query_key=" + window.config.search.queryKeyAutocomplete + "&search_query=" + suggestion,
            suggestionsCallback,
            true);
    }
}

function displayHistorySearchEntries() {
    hide(get("search-display-all-results") as HTMLAnchorElement)
    get("search-pane").classList.add("type-history")

    const searchSuggestionsContainer = get("suggestions-suggestions");
    const searchResultTemplate = get("template-search-history") as HTMLTemplateElement;
    searchSuggestionsContainer.innerHTML = "";
    if (searchResultTemplate.content.children.length) {
        $(".autocomplete__search-result").classList.add("autocomplete__search-result--active");
        $(".autocomplete__overlay").classList.add("autocomplete__overlay--active");
        Array.from(searchResultTemplate.content.children).forEach(x => {
            searchSuggestionsContainer.innerHTML += x.outerHTML
        });
        show(get("search-history-title"))
    }
    else {
        hide(get("search-history-title"))
    }
    $$("[data-search-suggestion]").forEach(x => {
        x.addEventListener("click", e => {
            e.preventDefault();
            e.stopPropagation();
            const row = (e.currentTarget as HTMLElement).parentElement;
            const idx = Array.from(searchSuggestionsContainer.children).indexOf(row);
            postData(window.config.removeSearchHistoryEntryUrl + "/" + idx)
            searchSuggestionsContainer.removeChild(row);
            const templateRow = searchResultTemplate.content.children[idx];
            searchResultTemplate.content.removeChild(templateRow)
        });
    });
}

function suggestCallback(suggestion: SearchNodeSuggestions) {
    const searchSuggestionsContainer = get("suggestions-suggestions");
    const searchResultTemplate = get("template-suggestion") as HTMLTemplateElement;
    const searchResultContainer = $(".autocomplete__search-result");

    searchResultContainer.classList.add("autocomplete__search-result--active");
    $(".autocomplete__overlay").classList.add("autocomplete__overlay--active");

    const searchValue = getSearchString(false);

    suggestionFacets(searchValue);

    searchSuggestionsContainer.innerHTML = "";
    if (suggestion.suggestions.length) {
        get("search-pane").classList.remove("type-history")
        suggestion.suggestions.forEach(x => {
            const searchResult = searchResultTemplate.content.cloneNode(true) as HTMLElement;
            const searchElement = searchResult.firstElementChild as HTMLAnchorElement;
            searchElement.href = window.config.search.rootSearchUrl + x.suggestion;
            searchResult.querySelector("[data-search-item-name]").innerHTML = x._highlight.suggestion;
            searchSuggestionsContainer.appendChild(searchResult);
        });        
    } else {
        get("search-pane").classList.add("type-history")
        displayHistorySearchEntries()
    }
}
function nostoSuggestCallback(suggestion: SearchNodeResults) {    
    const searchSuggestionsContainer = get("suggestions-suggestions");
    const searchResultTemplate = get("template-suggestion") as HTMLTemplateElement;
    const searchResultContainer = $(".autocomplete__search-result");

    searchResultContainer.classList.add("autocomplete__search-result--active");
    $(".autocomplete__overlay").classList.add("autocomplete__overlay--active");

    const searchValue = getSearchString(false);

    suggestionsCallback(suggestion);

    searchSuggestionsContainer.innerHTML = "";
    if (suggestion.suggestions?.length) {
        get("search-pane").classList.remove("type-history")
        suggestion.suggestions.forEach(x => {
            const searchResult = searchResultTemplate.content.cloneNode(true) as HTMLElement;
            const searchElement = searchResult.firstElementChild as HTMLAnchorElement;
            searchElement.href = window.config.search.rootSearchUrl + x;
            searchResult.querySelector("[data-search-item-name]").innerHTML = highlight(x, searchValue);            
            searchSuggestionsContainer.appendChild(searchResult);
        });        
    } else {
        get("search-pane").classList.add("type-history")
        displayHistorySearchEntries()
    }
}

function getSearchString(urlFriendly: boolean) {
    const searchKeyword = get("searchKeyword") as HTMLInputElement
    const searchValueSpaces = searchKeyword.value.replace(/'|´|,|\(|\)|\\|™|▪|°|©|\|/g, "").replace(/\+|\/|:| - |&|%|\?| -> |>|<| -- |\*/g, " ")
    const retVal = urlFriendly ? searchValueSpaces.replace(/( )+/g, "+") : searchValueSpaces
    return retVal
}

function search(event: Event) {
    event.preventDefault()
    const searchUrlTemplate = (event.target as HTMLFormElement).action;    
    
    var searchStr = getSearchString(true);

    if(window.config.search.nostoSearchUrl.length) {
        var organicSearchAPIjs = "nostojs(api => {api.recordSearchSubmit('" + searchStr + "')})"
        const script = document.createElement("script");
        script.innerHTML = organicSearchAPIjs;
        document.body.appendChild(script);
    }
    
    location.href = searchUrlTemplate + searchStr;
}

function suggestInternal() {
    const searchKeyword = get("searchKeyword") as HTMLInputElement
    const oldSearch = searchKeyword.getAttribute("data-old-search")

    const searchValue = getSearchString(true)

    if (oldSearch === searchValue) {
        return
    }
    else {
        searchKeyword.setAttribute("data-old-search", searchValue)
    }

    hide(get("suggestions-pane"))

    if (searchValue.length > 1) {
        if(window.config.search.nostoSearchUrl.length) {
            getDataJson(window.config.search.nostoSearchUrl + "/" + searchValue, nostoSuggestCallback, true);
        }
        else{
            withCategoryTreeIdLookup(() => getDataJson(window.config.search.searchNodeSuggestUrl + "?query_key=" + window.config.search.queryKeyAutocompleteKeywords + "&suggest_query=" + searchValue, suggestCallback, true));
        }
    }
    else {
        displayHistorySearchEntries()
    }
}

let timeout = 0

function suggestWithPacing() {
    if (timeout > 0) {
        window.clearTimeout(timeout)
    }
    timeout = window.setTimeout(suggestInternal, 200)
}

function responsiveLayout() {
    if (window.isResponsive) {
        if ($(".autocomplete__search-result--active")) {
            $(".autocomplete__wrapper").classList.add("visible");
        }
        
        return;
    }
    
    $(".autocomplete__wrapper").classList.remove("visible");
}

export function initSearch() {
    const searchForm = get("searchForm") as HTMLFormElement
    if (window.config.search.searchNodeSearchUrl) {
        searchForm.addEventListener("click", () => suggestWithPacing())
        searchForm.addEventListener("input", () => suggestWithPacing())
        searchForm.addEventListener("keydown", (e : KeyboardEvent) => {
            if (e.key === Key.ArrowDown || e.key === Key.ArrowUp) {
                e.preventDefault()
                const activeElement = document.activeElement as HTMLElement
                const searchInputElm = get('searchKeyword')
                const isInSearch = document.activeElement === searchInputElm
                const items = Array.from($$('#search-pane .search-result__item, #search-display-all-results'))
                const idx = isInSearch ? (e.key === Key.ArrowDown ? -1 : items.length) : items.indexOf(activeElement)
                const nextIdx = e.key === Key.ArrowDown ? idx + 1 : idx - 1
                
                if (nextIdx > items.length - 1 || nextIdx < 0) {
                    searchInputElm.focus()
                } else {
                    items[nextIdx].focus()
                }
            }
        })
    }
    searchForm.addEventListener("submit", x => search(x))

    window.responsiveObserverFunctions = window.responsiveObserverFunctions || [];
    window.responsiveObserverFunctions.push(responsiveLayout)
}

function withCategoryTreeIdLookup(callback: Function) {
    if (categoryTreeIdLookup != null) {
        callback();
        return
    };
    getData(window.config.getCategoryTreeIdToUrl, (x: Response) => {
        x.json().then((x: CategoryTreeIdToUrl) => {
            categoryTreeIdLookup = x;
            callback();
        })
    }, true)
}

function registerRecordSearchClick(code: string, url: string) {
    
    const searchClickJS = "nostojs(api => api.recordSearchClick('autocomplete',{ productId: '" + code + "', url: '" + url + "' }))";
    const script = document.createElement("script");
    script.innerHTML = searchClickJS;
    return script;
}

function registerRecordSearch(results: SearchNodeResults) {
    
    const hitsCodes = "[" + results?.docs.map(x => "{ productId: '" + x.code + "' }").join(",") + "]";
    const recordAutocompleteJS = "nostojs(api => api.recordSearch(" +
        "'autocomplete'," +
        "{query: '" + results.search_query + "'}," +
        "{" +
            "products: {" +
            "hits: " + hitsCodes + ", " +
            "fuzzy: " + results.nosto_record_search_dto.response.fuzzy + ", " +
            "total: " + results.nosto_record_search_dto.response.total + ", " +
            "size: " + results.nosto_record_search_dto.response.size + "," +
            "from: " + results.nosto_record_search_dto.response.from + "," +
            "}" +
        "}," +
        "{ isKeyword: false}" +
        "))";

    const script = document.createElement("script");
    script.innerHTML = recordAutocompleteJS;
    
    return script;    
}